import styled, { css } from 'styled-components'
import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Form from 'react-bootstrap/cjs/Form'
import Tabs from 'react-bootstrap/cjs/Tabs'
import Tab from 'react-bootstrap/cjs/Tab'
import ListGroup from 'react-bootstrap/cjs/ListGroup'
import { setSearch, selectNetworkSearch } from '../networkingSlice'
import { searchMeetingsSelector } from '../videocallsSlice'
import colors from '../../../constants/colors'
import { _ } from '../../../utils/localization'
import Icon from '../../../components/Icon'
import VideocallListItem from './VideocallListItem'
import CurrentUserIcon from './CurrentUserIcon'
import AttendeeListItem from './AttendeeListItem'
import { User } from '../../auth/types'
import useSlug from '../../../hooks/useSlug'
import useDebounce from '../../../hooks/useDebounce'
import styles from '../../../constants/styles'
import { toggleProfile } from '../../profile/editProfileSlice'
import {
    publicAttendeesSelector,
    canLoadMoreSelector,
    getAttendeeList,
    loadMore,
    selectAttendeeOffset,
    selectAttendeeStatus,
    getAttendeeListWithSearch,
    clearOffsetSearch,
    attendeesSelector,
} from '../../attendees/attendeesSlice'
import LoadMore from '../../../components/LoadMore'
import Loader from '../../../components/Loader'
import formatJobInfo from '../../../utils/formatJobInfo'
import useSubscribeAttendees from '../../attendees/useSubscribeAttendees'
import PublicChatBar from '../../../components/PublicChatBar'
import ShowMeToggle from '../../events/ShowMeToggle'

const mediumGray = colors.gray5
const lightGray = colors.gray3

const S = {
    SideBar: styled.div`
      background: white;
      width: ${styles.sidebarWidth}px;
      height: 100%;
      position: fixed;
      right: 0;
      @media (max-width: 1366px) {
          width: ${styles.smallerSidebarWidth}px;
      }
`,
    SideBarTop: styled.div`
      background: ${colors.mineShaft};
      width: 100%;
`,
    Instructions: styled.div`
      background: white;
      font-size: 11px;
      text-align: center;
      padding: 4px;
`,
    IconWrapper: styled.div`
      position: relative;
      width: 55px;
      height: 55px;
    `,
    CurrentUser: styled.div`
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      color: ${lightGray};
      padding-right: 10px;
      padding-left: 3px;
      padding-top: 12px;
      line-height: 100%;
    `,
    UserInfoWrapper: styled.div`
      display: flex;
      flex-direction: row;
    `,
    CurrentUserInfo: styled.div`
      display: flex;
      justify-content: flex-start;
      flex-direction: column;
      width: 220px;
      @media (max-width: 1366px) {
          width: 150px;
      }
    `,
    Name: styled.div`
      font-size: 15px;
      font-weight: 600;
      line-height: 20px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `,
    JobDescription: styled.div`
      font-size: 12px;
      color: ${mediumGray};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `,
    EditProfileLink: styled.span`
      font-size: 12px;
      color: ${colors.orange};
      text-decoration: underline;
      cursor: pointer;
      &:hover{
          color: ${colors.orange};
          text-decoration: none;
      }
    `,
    InputGroup: styled.div`
        position: relative;
        padding: 8px 8px 10px 8px;
    `,
    SearchIcon: styled.div`
        position: absolute;
        top: 50%;
        right: 15px;
        transform: translateY(-53%);
    `,
    Tabs: styled(Tabs)`
        & a {
            width: 175px;
            @media (max-width: 1366px) {
                width: 140px;
            }
            border-radius: 0!important;
            outline: none;
            &:focus {
                outline: none!important;
            }
            &:not([class=active]) {
                background-color: ${colors.mineShaft};
                color: ${mediumGray}!important;
            }
        }
    `,
    TabTitle: styled.span`
        display: flex;
        align-items: center;
        font-size: 15px;
    `,
    ListGroup: styled(ListGroup)`
        height: calc(100vh - ${styles.topNavHeight}px - 200px); // 195px of top action space + 5 for the timetable
        padding-bottom: 16px;
        overflow-y: auto;
    `,
    SwitchSidebar: styled.div`
        position: absolute;
        left: 0;
        top: 50%;
        width: 36px;
        height: 120px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background-color: white;
        border-radius: 8px 0 0 8px;
        transform: translate(-100%, -50%);
        box-shadow: -2px 1px 2px rgba(0, 0, 0, 0.1);
    `,
    ActionIcon: styled.div<{isActive: boolean}>`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 50px;
        border: 3px solid transparent;
        cursor: pointer;
        &:hover {
            background: ${colors.gray2};
        }
        ${({ isActive }) => isActive && css`
            background: ${colors.gray2};
            border-right-color: gray;
        `}
    `
}

const MeetingList = () => {
    const meetings = useSelector(searchMeetingsSelector())
    return (
        <S.ListGroup>
            {' '}
            {
                meetings.map((meeting) => (
                    <VideocallListItem key={meeting.id} meeting={meeting} />
                ))
            }
        </S.ListGroup>
    )
}

export const AttendeeList = () => {
    const dispatch = useDispatch()
    const eventSlug = useSlug('eventSlug')
    const requestStatus = useSelector(selectAttendeeStatus)
    const search = useSelector(selectNetworkSearch)
    const offset = useSelector(selectAttendeeOffset((!!search)))
    const canLoadMore = useSelector(canLoadMoreSelector(!!search))
    const publicAttendees = useSelector(publicAttendeesSelector(!!search))
    const attendees = useSelector(attendeesSelector(!!search))
    useSubscribeAttendees(attendees.map((attendee) => attendee.id))

    // Fetch attendee list
    useEffect(() => {
        if (search) {
            dispatch(getAttendeeListWithSearch({ eventSlug, offset, search }))
            return
        }
        dispatch(clearOffsetSearch())
        dispatch(getAttendeeList({ eventSlug, offset }))
    }, [dispatch, eventSlug, search, offset])

    return (
        <S.ListGroup>
            {
                publicAttendees.map((attendee) => (
                    <AttendeeListItem
                        attendee={attendee}
                        key={attendee.id}
                    />
                ))
            }
            {requestStatus === 'loading' && <Loader />}
            {canLoadMore
            && (
                <LoadMore onClick={() => dispatch(loadMore(!!search))}>
                    {_('networking.sidebar.load_more_attendees')}
                </LoadMore>
            )}
        </S.ListGroup>
    )
}

const CurrentUser = ({ currentUser }: { currentUser: User }) => {
    const dispatch = useDispatch()
    const onClick = () => {
        dispatch(toggleProfile())
    }
    return (
        <S.CurrentUserInfo>
            <S.Name>
                {currentUser.first_name}
                {' '}
                {currentUser.last_name}
            </S.Name>
            <S.JobDescription>
                {formatJobInfo(currentUser.jobtitle, currentUser.company)}
            </S.JobDescription>
            <S.EditProfileLink onClick={onClick}>{_('networking.sidebar.edit_profile')}</S.EditProfileLink>
        </S.CurrentUserInfo>
    )
}

const SideBar = ({ currentUser, chatId }: { currentUser: User, chatId?: number }) => {
    const dispatch = useDispatch()
    const [isDefaultSidebar, setDefaultSidebar] = useState(true)
    const debounceSearch = useDebounce((value) => dispatch(setSearch(value)), 400)
    const handleSearch = (e) => {
        debounceSearch(e.target.value)
    }

    return (
        <S.SideBar>
            {chatId && (
                <S.SwitchSidebar>
                    <S.ActionIcon isActive={isDefaultSidebar} onClick={() => setDefaultSidebar(true)}>
                        <Icon icon="personCircle" />
                    </S.ActionIcon>
                    <S.ActionIcon isActive={!isDefaultSidebar} onClick={() => setDefaultSidebar(false)}>
                        <Icon icon="forum" />
                    </S.ActionIcon>
                </S.SwitchSidebar>
            )}

            <S.SideBarTop>
                <S.Instructions>{_('networking.sidebar.drag_instructions')}</S.Instructions>
                <S.CurrentUser>
                    <S.UserInfoWrapper>
                        <S.IconWrapper>
                            <CurrentUserIcon left={0} top={0} />
                        </S.IconWrapper>
                        <CurrentUser currentUser={currentUser} />
                    </S.UserInfoWrapper>

                    <ShowMeToggle />
                </S.CurrentUser>

                <S.InputGroup>
                    <Form.Control
                        placeholder={_('networking.sidebar.search_placeholder')}
                        onChange={handleSearch}
                    />
                    <S.SearchIcon>
                        <Icon icon="search" size={22} />
                    </S.SearchIcon>
                </S.InputGroup>
            </S.SideBarTop>

            <S.Tabs defaultActiveKey="attendees" id="uncontrolled-tab-example">
                <Tab
                    eventKey="attendees"
                    title={(
                        <S.TabTitle>
                            <Icon icon="personCircle" size={22} style={{ marginRight: 5 }} />
                            {' '}
                            {_('networking.sidebar.attendees_tab_title')}
                        </S.TabTitle>
                    )}
                >
                    <AttendeeList />
                </Tab>
                <Tab
                    eventKey="meetings"
                    title={(
                        <S.TabTitle>
                            <Icon icon="groupCircle" size={22} style={{ marginRight: 5 }} />
                            {' '}
                            {_('networking.sidebar.meetings_tab_title')}
                        </S.TabTitle>
                    )}
                >
                    <MeetingList />
                </Tab>
            </S.Tabs>
            {chatId && (
                <PublicChatBar
                    isShowPublicChat={!isDefaultSidebar}
                    chatId={chatId}
                    header={_('networking.public_chat.title')}
                />
            )}
        </S.SideBar>
    )
}

export default SideBar
